<script setup lang="ts">
import { ref, computed } from 'vue';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/toast';
import AppNav from '@/components/AppNav.vue';
import AppFooter from '@/components/AppFooter.vue';
import { i18n, t } from '@/i18n';
import { getApiBaseUrl } from '@/composables/useApi';
import { withSsrDependencyTiming } from '@/lib/ssr-timing';
import { Loader2 } from 'lucide-vue-next';
import { buildStripeCheckoutSuccessUrl } from '@seede/ui/pricing';
import { Amplitude } from '@seede/amplitude';
import {
  absoluteLocalizedUrl,
  buildPathHreflangLinks,
  ogLocaleFor,
  siteLocaleFromRouteParam,
} from '@/lib/site-locale';

/** Strip path/query from a URL — only the host is safe for analytics. */
function safeUrlHost(u: string | undefined | null): string {
  if (!u) return '';
  try {
    return new URL(u).host;
  } catch {
    return '';
  }
}

const { isAuthenticated } = useAuth();
const { request } = useApi();
const { toast } = useToast();
const router = useRouter();

// Billing period toggle (monthly/yearly)
const billingPeriod = ref<'monthly' | 'yearly'>('yearly');

// Selected credit tier for Pro plan
const selectedCreditTier = ref<number>(6000);

// Available credit tiers for Pro plan dropdown (from 定价.md)
type PlanId = 'free' | 'pro' | 'ultra';

type PricingFeature = {
  text: string;
  highlight: boolean;
  sub?: boolean;
};

type PricingTierCredits = {
  daily: number;
  monthly: number;
  bonus: number;
};

type PricingTier = {
  credits: PricingTierCredits;
  monthlyPrice: number;
  yearlyPrice: number;
  originalMonthlyPrice: number;
};

type PricingPlan = {
  id: PlanId;
  name: string;
  description: string;
  cta: string;
  ctaLink: string;
  popular: boolean;
  features: PricingFeature[];
  tiers: PricingTier[];
};

type ProCreditTier = {
  credits: number;
  monthlyPrice: number;
  originalMonthlyPrice: number;
  yearlyPrice: number;
};

const proCreditTiers = ref<ProCreditTier[]>([
  {
    credits: 6000,
    monthlyPrice: 20,
    originalMonthlyPrice: 39,
    yearlyPrice: 200,
  },
  {
    credits: 12000,
    monthlyPrice: 40,
    originalMonthlyPrice: 78,
    yearlyPrice: 400,
  },
  {
    credits: 18000,
    monthlyPrice: 60,
    originalMonthlyPrice: 117,
    yearlyPrice: 600,
  },
]);

const ultraTier = ref({
  credits: 40000,
  monthlyPrice: 120,
  originalMonthlyPrice: 260,
  yearlyPrice: 1200,
});

// Fetch pricing plans from API
const { data: pricingData } = await useAsyncData('pricing-plans', async () => {
  try {
    const baseUrl = getApiBaseUrl();
    return await withSsrDependencyTiming(
      'api:pricing:plans',
      () => $fetch(`${baseUrl}/pricing/plans`),
      { url: `${baseUrl}/pricing/plans`, kind: 'external' }
    );
  } catch (error) {
    console.error('Failed to fetch pricing plans:', error);
    return null;
  }
});

if (pricingData.value) {
  const data = pricingData.value as any;

  // Update Pro tiers
  if (data.pro_l1_monthly && data.pro_l2_monthly && data.pro_l3_monthly) {
    proCreditTiers.value = [
      {
        credits: data.pro_l1_monthly.credits,
        monthlyPrice: data.pro_l1_monthly.price,
        originalMonthlyPrice: data.pro_l1_monthly.originalPrice,
        yearlyPrice: data.pro_l1_yearly.price,
      },
      {
        credits: data.pro_l2_monthly.credits,
        monthlyPrice: data.pro_l2_monthly.price,
        originalMonthlyPrice: data.pro_l2_monthly.originalPrice,
        yearlyPrice: data.pro_l2_yearly.price,
      },
      {
        credits: data.pro_l3_monthly.credits,
        monthlyPrice: data.pro_l3_monthly.price,
        originalMonthlyPrice: data.pro_l3_monthly.originalPrice,
        yearlyPrice: data.pro_l3_yearly.price,
      },
    ];
    // Update default selected tier if needed (e.g. to the first one)
    if (!proCreditTiers.value.find((t) => t.credits === selectedCreditTier.value)) {
      selectedCreditTier.value = proCreditTiers.value[0]!.credits;
    }
  }

  // Update Ultra tier
  if (data.ultra_monthly && data.ultra_yearly) {
    ultraTier.value = {
      credits: data.ultra_monthly.credits,
      monthlyPrice: data.ultra_monthly.price,
      originalMonthlyPrice: data.ultra_monthly.originalPrice,
      yearlyPrice: data.ultra_yearly.price,
    };
  }
}

const plans = computed<PricingPlan[]>(() => [
  {
    id: 'free',
    name: t('pricing.page.plans.free.name'),
    description: t('pricing.page.plans.free.description'),
    cta: t('pricing.page.plans.free.cta'),
    ctaLink: isAuthenticated.value ? '/workspace/create' : '/signup',
    popular: false,
    tiers: [
      {
        credits: {
          daily: 100,
          monthly: 0,
          bonus: 300,
        },
        monthlyPrice: 0,
        yearlyPrice: 0,
        originalMonthlyPrice: 0,
      },
    ],
    features: [
      { text: t('pricing.page.plans.free.features.fullFeaturedEditor'), highlight: true },
      { text: t('pricing.page.plans.free.features.standardExport'), highlight: false },
      { text: t('pricing.page.plans.free.features.uploadLimit'), highlight: false },
    ],
  },
  {
    id: 'pro',
    name: t('pricing.page.plans.pro.name'),
    description: t('pricing.page.plans.pro.description'),
    cta: t('pricing.page.plans.pro.cta'),
    ctaLink: '/signup',
    popular: true,
    tiers: proCreditTiers.value.map((tier) => ({
      credits: {
        daily: 100,
        monthly: tier.credits,
        bonus: 0,
      },
      monthlyPrice: tier.monthlyPrice,
      yearlyPrice: tier.yearlyPrice,
      originalMonthlyPrice: tier.originalMonthlyPrice,
    })),
    features: [
      { text: t('pricing.page.plans.pro.features.removeBadge'), highlight: true },
      { text: t('pricing.page.plans.pro.features.commercialLicense'), highlight: true },
      { text: t('pricing.page.plans.pro.features.creditRollovers'), highlight: true },
      { text: t('pricing.page.plans.pro.features.advancedExport'), highlight: true },
      { text: t('pricing.page.plans.pro.features.exportFormats'), highlight: false, sub: true },
      { text: t('pricing.page.plans.pro.features.higherQuality'), highlight: false },
      { text: t('pricing.page.plans.pro.features.uploadLimit'), highlight: false },
      { text: t('pricing.page.plans.pro.features.multiPage'), highlight: false },
      { text: t('pricing.page.plans.pro.features.earlyAccess'), highlight: false },
      { text: t('pricing.page.plans.pro.features.dedicatedSupport'), highlight: false },
    ],
  },
  {
    id: 'ultra',
    name: t('pricing.page.plans.ultra.name'),
    description: t('pricing.page.plans.ultra.description'),
    cta: t('pricing.page.plans.ultra.cta'),
    ctaLink: '/signup',
    popular: false,
    tiers: [
      {
        credits: {
          daily: 100,
          monthly: ultraTier.value?.credits || 0,
          bonus: 0,
        },
        monthlyPrice: ultraTier.value.monthlyPrice,
        yearlyPrice: ultraTier.value.yearlyPrice,
        originalMonthlyPrice: ultraTier.value.originalMonthlyPrice,
      },
    ],
    features: [
      { text: t('pricing.page.plans.ultra.features.everythingInPro'), highlight: true },
      { text: t('pricing.page.plans.ultra.features.brandKit'), highlight: true },
      { text: t('pricing.page.plans.ultra.features.apiAccess'), highlight: false },
      { text: t('pricing.page.plans.ultra.features.unlimitedStorage'), highlight: false },
      { text: t('pricing.page.plans.ultra.features.customContracts'), highlight: false },
      { text: t('pricing.page.plans.ultra.features.earlyAccess'), highlight: false },
      { text: t('pricing.page.plans.ultra.features.prioritySupport'), highlight: false },
    ],
  },
]);

// FAQ items from 定价.md
const faqItems = computed(() => [
  {
    question: t('pricing.page.faq.items.whatIsVeesoAI.question'),
    answerParagraph1: t('pricing.page.faq.items.whatIsVeesoAI.answerParagraph1'),
    answerParagraph2: t('pricing.page.faq.items.whatIsVeesoAI.answerParagraph2'),
    answerParagraph3: t('pricing.page.faq.items.whatIsVeesoAI.answerParagraph3'),
    products: (() => {
      const products = i18n.getRaw('pricing.page.faq.items.whatIsVeesoAI.products');
      return Array.isArray(products) ? (products as string[]) : [];
    })(),
  },
  {
    question: t('pricing.page.faq.items.whatIsCredit.question'),
    answerParagraph1: t('pricing.page.faq.items.whatIsCredit.answerParagraph1'),
    answerParagraph2: t('pricing.page.faq.items.whatIsCredit.answerParagraph2'),
    answerParagraph3: t('pricing.page.faq.items.whatIsCredit.answerParagraph3'),
  },
  {
    question: t('pricing.page.faq.items.getMoreCredits.question'),
    answerParagraph1: t('pricing.page.faq.items.getMoreCredits.answerParagraph1'),
    listItems: [
      t('pricing.page.faq.items.getMoreCredits.answerParagraph2'),
      t('pricing.page.faq.items.getMoreCredits.answerParagraph3'),
      t('pricing.page.faq.items.getMoreCredits.answerParagraph4'),
      t('pricing.page.faq.items.getMoreCredits.answerParagraph5'),
      t('pricing.page.faq.items.getMoreCredits.answerParagraph6'),
    ],
  },
  {
    question: t('pricing.page.faq.items.creditExpiry.question'),
    answerParagraph1: t('pricing.page.faq.items.creditExpiry.answerParagraph1'),
    listItems: (() => {
      const listItemsData = i18n.getRaw('pricing.page.faq.items.creditExpiry.listItems');
      if (listItemsData && typeof listItemsData === 'object') {
        const listItems = listItemsData as Record<string, string>;
        return [
          listItems.dailyCredits,
          listItems.newUserBonus,
          listItems.referralCredits,
          listItems.promotionCredits,
          listItems.monthlySubscription,
          listItems.yearlySubscription,
        ].filter(Boolean);
      }
      return [];
    })(),
  },
  {
    question: t('pricing.page.faq.items.creditConsumption.question'),
    answerParagraph1: t('pricing.page.faq.items.creditConsumption.answerParagraph1'),
    listItems: (() => {
      const listItemsData = i18n.getRaw('pricing.page.faq.items.creditConsumption.listItems');
      if (listItemsData && typeof listItemsData === 'object') {
        const listItems = listItemsData as Record<string, string>;
        return [
          listItems.dailyCredits,
          listItems.promotionCredits,
          listItems.referralCredits,
          listItems.newUserBonus,
          listItems.subscriptionCredits,
        ].filter(Boolean);
      }
      return [];
    })(),
  },
  {
    question: t('pricing.page.faq.items.inviteFriends.question'),
    answerParagraph1: t('pricing.page.faq.items.inviteFriends.answerParagraph1'),
    link: (() => {
      const linkData = i18n.getRaw('pricing.page.faq.items.inviteFriends.link');
      return linkData || null;
    })(),
    answerParagraph2: t('pricing.page.faq.items.inviteFriends.answerParagraph2'),
  },
  {
    question: t('pricing.page.faq.items.designOwnership.question'),
    answer: t('pricing.page.faq.items.designOwnership.answer'),
  },
  {
    question: t('pricing.page.faq.items.commercialUse.question'),
    answerParagraph1: t('pricing.page.faq.items.commercialUse.answerParagraph1'),
    answerParagraph2: t('pricing.page.faq.items.commercialUse.answerParagraph2'),
  },
  {
    question: t('pricing.page.faq.items.studentDiscounts.question'),
    answer: t('pricing.page.faq.items.studentDiscounts.answer'),
  },
  {
    question: t('pricing.page.faq.items.contactTeam.question'),
    answer: t('pricing.page.faq.items.contactTeam.answer'),
    link: (() => {
      const linkData = i18n.getRaw('pricing.page.faq.items.contactTeam.link');
      return linkData || null;
    })(),
    answerAfterLink: t('pricing.page.faq.items.contactTeam.answerAfterLink'),
  },
  // Temporarily hidden
  // {
  //   question: t('pricing.page.faq.items.creditUsage.question'),
  //   answer: t('pricing.page.faq.items.creditUsage.answer'),
  //   table: (() => {
  //     // Get table data directly from imported en.js
  //     const tableData = en.pricing?.page?.faq?.items?.creditUsage?.table;
  //     if (!tableData || !tableData.headersArray || !tableData.rows) {
  //       // Fallback - use default headers from i18n
  //       return {
  //         headers: [
  //           t('pricing.page.faq.items.creditUsage.table.headers.feature'),
  //           t('pricing.page.faq.items.creditUsage.table.headers.mode'),
  //           t('pricing.page.faq.items.creditUsage.table.headers.creditsPerTime'),
  //         ],
  //         rows: [],
  //       };
  //     }

  //     // Original rows data from i18n
  //     const originalRows = tableData.rows;

  //     // Process rows to add rowspan information for first column
  //     const processedRows: Array<Array<string | { text: string; rowspan: number }>> = [];
  //     let currentFeature = '';
  //     let featureStartIndex = -1;
  //     let featureCount = 0;

  //     originalRows.forEach((row: string[]) => {
  //       const feature = row[0] || '';
  //       if (feature !== currentFeature) {
  //         // New feature group - close previous group
  //         if (currentFeature !== '' && featureStartIndex >= 0 && featureCount > 1) {
  //           const firstRow = processedRows[featureStartIndex];
  //           if (firstRow) {
  //             firstRow[0] = { text: currentFeature, rowspan: featureCount };
  //           }
  //         }
  //         // Start new feature group
  //         currentFeature = feature || '';
  //         featureStartIndex = processedRows.length;
  //         featureCount = 1;
  //         processedRows.push([...row]);
  //       } else {
  //         // Same feature - increment count and add row without first column
  //         featureCount++;
  //         processedRows.push([...row.slice(1)]);
  //       }
  //     });

  //     // Handle last feature group
  //     if (currentFeature !== '' && featureStartIndex >= 0 && featureCount > 1) {
  //       const firstRow = processedRows[featureStartIndex];
  //       if (firstRow) {
  //         firstRow[0] = { text: currentFeature, rowspan: featureCount };
  //       }
  //     }

  //     return {
  //       headers: tableData.headersArray || tableData.headers || [],
  //       rows: processedRows,
  //     };
  //   })(),
  // },
  {
    question: t('pricing.page.faq.items.tieredPricing.question'),
    answer: t('pricing.page.faq.items.tieredPricing.answer'),
  },
  {
    question: t('pricing.page.faq.items.creditRollovers.question'),
    answer: t('pricing.page.faq.items.creditRollovers.answer'),
  },
  {
    question: t('pricing.page.faq.items.upgradingDowngrading.question'),
    answer: t('pricing.page.faq.items.upgradingDowngrading.answer'),
  },
  {
    question: t('pricing.page.faq.items.cancelSubscription.question'),
    answer: t('pricing.page.faq.items.cancelSubscription.answer'),
  },
  {
    question: t('pricing.page.faq.items.getInvoice.question'),
    answer: t('pricing.page.faq.items.getInvoice.answer'),
  },
  {
    question: t('pricing.page.faq.items.planReset.question'),
    answer: t('pricing.page.faq.items.planReset.answer'),
  },
  {
    question: t('pricing.page.faq.items.dailyCreditsReset.question'),
    answer: t('pricing.page.faq.items.dailyCreditsReset.answer'),
  },
]);

// Helper functions for feature rendering (matching poster style)
function isSubFeature(text: string): boolean {
  const trimmed = text.trim();
  const lowerText = trimmed.toLowerCase();
  return (
    lowerText.startsWith('text:') ||
    lowerText.startsWith('images:') ||
    lowerText.startsWith('remix') ||
    lowerText.startsWith('edit') ||
    lowerText.startsWith('png') ||
    lowerText.startsWith('psd') ||
    trimmed.startsWith('•')
  );
}

// Computed values
const getCurrentTier = (plan: PricingPlan): PricingTier => {
  if (plan.id === 'pro') {
    return plan.tiers.find((t) => t.credits.monthly === selectedCreditTier.value) || plan.tiers[0]!;
  }
  return plan.tiers[0]!;
};

const currentPrice = computed(() => {
  return (plan: PricingPlan) => {
    const tier = getCurrentTier(plan);
    if (billingPeriod.value === 'monthly') {
      return tier.monthlyPrice;
    }
    return Math.round((tier.yearlyPrice / 12) * 100) / 100;
  };
});

const _originalPrice = computed(() => {
  return (plan: PricingPlan) => {
    const tier = getCurrentTier(plan);
    if (tier.originalMonthlyPrice <= 0) return 0;
    return tier.originalMonthlyPrice;
  };
});

const currentCredits = computed(() => {
  return (plan: PricingPlan) => {
    const tier = getCurrentTier(plan);
    return {
      daily: tier.credits?.daily || 0,
      monthly: tier.credits?.monthly || 0,
      bonus: tier.credits?.bonus || 0,
    };
  };
});

// Get current monthly credits for Pro plan
const currentMonthlyCredits = computed(() => {
  return selectedCreditTier.value;
});

const formatPrice = (price: number) => {
  if (price === 0) return '$0';
  if (Number.isInteger(price)) {
    return `$${price.toLocaleString()}`;
  }
  return `$${price.toFixed(2)}`;
};

const yearlySavings = computed(() => {
  return (plan: PricingPlan) => {
    if (plan.id === 'free') return 0;
    const tier = getCurrentTier(plan);
    if (tier.monthlyPrice <= 0) return 0;
    return Math.max(0, tier.monthlyPrice * 12 - tier.yearlyPrice);
  };
});

const _yearlyDiscount = computed(() => {
  return (plan: PricingPlan) => {
    if (plan.id === 'free') return 0;
    const tier = getCurrentTier(plan);
    const originalYearlyPrice = tier.originalMonthlyPrice * 12;
    if (originalYearlyPrice <= 0) return 0;
    const discount = ((originalYearlyPrice - tier.yearlyPrice) / originalYearlyPrice) * 100;
    return Math.max(0, Math.round(discount * 10) / 10);
  };
});

const _monthlyDiscount = computed(() => {
  return (plan: PricingPlan) => {
    if (plan.id === 'free') return 0;
    const tier = getCurrentTier(plan);
    if (tier.originalMonthlyPrice <= 0) return 0;
    const discount =
      ((tier.originalMonthlyPrice - tier.monthlyPrice) / tier.originalMonthlyPrice) * 100;
    return Math.max(0, Math.round(discount * 10) / 10);
  };
});

const loadingPlanId = ref<string | null>(null);

async function handleSubscribe(plan: PricingPlan) {
  if (!isAuthenticated.value) {
    router.push('/signup');
    return;
  }

  if (plan.id === 'free') {
    router.push('/signup');
    return;
  }

  loadingPlanId.value = plan.id;

  try {
    let planKey = '';
    if (plan.id === 'pro') {
      // Determine plan key
      // Map selectedCreditTier to level
      const tierIndex = proCreditTiers.value.findIndex(
        (t) => t.credits === selectedCreditTier.value
      );
      if (tierIndex === -1) {
        throw new Error(t('pricing.page.checkout.invalidTier'));
      }
      const level = tierIndex + 1;
      planKey = `pro_l${level}_${billingPeriod.value}`;
    } else if (plan.id === 'ultra') {
      planKey = `ultra_${billingPeriod.value}`;
    } else {
      throw new Error(t('pricing.page.checkout.invalidPlan'));
    }

    const successUrl = buildStripeCheckoutSuccessUrl();
    const res: any = await request('/checkout', {
      method: 'POST',
      body: {
        provider: 'stripe',
        plan: planKey,
        return: 'json',
        successUrl,
      },
    });

    const url = res?.url;
    if (url) {
      Amplitude.event('checkout_initiated', {
        source: 'pricing_page',
        provider: 'stripe',
        plan_id: plan.id,
        plan_key: planKey,
        billing_period: billingPeriod.value,
        monthly_credits:
          plan.id === 'pro' ? selectedCreditTier.value : (ultraTier.value?.credits ?? 0),
        display_price: currentPrice.value(plan),
        // Strip URL query/path — only host is safe to send (the rest may
        // carry user email, session token or return_to with PII).
        checkout_host: safeUrlHost(url),
        success_host: safeUrlHost(successUrl),
        page_path: window.location.pathname,
      });
      window.location.href = url;
    } else {
      throw new Error(t('pricing.page.checkout.noUrl'));
    }
  } catch (e: any) {
    console.error('Checkout error:', e);
    const msg = e?.data?.error || e?.message || t('pricing.page.checkout.failedToStart');
    toast({
      title: t('common.error'),
      description: msg,
      variant: 'destructive',
    });
  } finally {
    loadingPlanId.value = null;
  }
}

// SEO Meta and Structured Data
const route = useRoute();
const siteLocale = computed(() => siteLocaleFromRouteParam(route.params.locale));
const canonicalUrl = computed(() => absoluteLocalizedUrl(siteLocale.value, '/pricing'));
const seoTitle = computed(() => t('pricing.seoTitle'));
const seoDescription = computed(() => t('pricing.seoDescription'));
const seoKeywords = computed(() => t('pricing.seoKeywords'));

// FAQ Schema - Extract FAQ items for structured data
const faqSchemaItems = computed(() => {
  return faqItems.value
    .map((item) => {
      // Extract answer text from various formats
      let answerText = '';
      if (item.answer) {
        answerText = item.answer;
      } else if (item.answerParagraph1) {
        answerText = item.answerParagraph1;
        if (item.answerParagraph2) {
          answerText += ' ' + item.answerParagraph2;
        }
        if (item.answerParagraph3) {
          answerText += ' ' + item.answerParagraph3;
        }
        if ((item as any).link) {
          answerText += ' ' + (item as any).link.text;
        }
        if ((item as any).answerAfterLink) {
          answerText += ' ' + (item as any).answerAfterLink;
        }
      } else if ((item as any).listItems && Array.isArray((item as any).listItems)) {
        answerText = (item as any).listItems.join(' ');
      }
      return {
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answerText || item.question,
        },
      };
    })
    .filter((item) => item.acceptedAnswer.text);
});

const structuredData: any[] = [];

// FAQ Schema
if (faqSchemaItems.value.length > 0) {
  structuredData.push({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqSchemaItems.value,
  });
}

// Breadcrumb Schema
structuredData.push({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: t('pricing.breadcrumb.home'),
      item: absoluteLocalizedUrl(siteLocale.value, '/'),
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: t('pricing.breadcrumb.pricing'),
      item: canonicalUrl.value,
    },
  ],
});

const requestURL = useRequestURL();
const ogImageUrl = computed(() => `${requestURL.origin}/Veeso_og.jpg`);

useHead(() => ({
  title: seoTitle.value,
  htmlAttrs: { lang: siteLocale.value },
  meta: [
    {
      name: 'description',
      content: seoDescription.value,
    },
    {
      name: 'keywords',
      content: seoKeywords.value,
    },
    { property: 'og:title', content: seoTitle.value },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: canonicalUrl.value },
    { property: 'og:locale', content: ogLocaleFor(siteLocale.value) },
    {
      property: 'og:description',
      content: seoDescription.value,
    },
    { property: 'og:image', content: ogImageUrl.value },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: '@veesoai' },
    { name: 'twitter:title', content: seoTitle.value },
    {
      name: 'twitter:description',
      content: seoDescription.value,
    },
    { name: 'twitter:image', content: ogImageUrl.value },
  ],
  link: [{ rel: 'canonical', href: canonicalUrl.value }, ...buildPathHreflangLinks('/pricing')],
  script: structuredData.map((schema) => ({
    type: 'application/ld+json',
    innerHTML: JSON.stringify(schema),
  })),
}));
</script>

<template>
  <div class="min-h-screen pt-[60px] relative">
    <AppNav active-path="/pricing" />

    <!-- Main Content -->
    <div class="py-16 px-4 relative z-10">
      <!-- Hero Section -->
      <div class="max-w-[1320px] mx-auto mb-16">
        <div class="text-center mb-16">
          <h1 class="text-4xl md:text-6xl font-semibold tracking-tight text-black/80 mb-8">
            {{ t('pricing.page.title') }}
          </h1>
          <!-- Billing Period Toggle -->
          <div class="flex items-center justify-center mb-8">
            <div class="inline-flex items-center gap-1 bg-black/5 p-1 rounded-full">
              <button
                @click="billingPeriod = 'monthly'"
                :class="[
                  'relative px-5 py-2 text-sm font-medium rounded-full transition-all duration-200 focus:outline-none',
                  billingPeriod === 'monthly'
                    ? 'bg-white text-black/90 shadow-sm'
                    : 'text-black/50 hover:text-black/70',
                ]"
              >
                {{ t('pricing.page.billingPeriod.monthly') }}
              </button>
              <button
                @click="billingPeriod = 'yearly'"
                :class="[
                  'relative pl-5 pr-1 py-1 text-sm font-medium rounded-full transition-all duration-200 focus:outline-none flex items-center gap-2',
                  billingPeriod === 'yearly'
                    ? 'bg-white text-black/90 shadow-sm'
                    : 'text-black/50 hover:text-black/70',
                ]"
              >
                {{ t('pricing.page.billingPeriod.yearly') }}
                <span class="text-sm bg-black/5 text-black/90 px-2 py-1 rounded-full font-medium">
                  {{ t('pricing.page.billingPeriod.saveUpTo') }}
                </span>
              </button>
            </div>
          </div>
        </div>

        <!-- Pricing Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-24">
          <div
            v-for="plan in plans"
            :key="plan.id"
            class="rounded-3xl transition-all duration-300 flex flex-col bg-white"
            :class="{ 'md:col-span-2 lg:col-span-1': plan.id === 'ultra' }"
          >
            <div class="px-6 pb-6 pt-4 flex-1 flex flex-col">
              <div>
                <div class="flex items-center justify-between">
                  <h2 class="text-2xl text-black/80 flex items-center gap-1.5">
                    {{ plan.name }}
                    <template v-if="plan.popular">
                      <span class="text-black/20">·</span>
                      <span class="popular-badge text-2xl font-normal">{{
                        t('pricing.popular')
                      }}</span>
                    </template>
                  </h2>
                </div>
                <p class="text-lg text-black/50 mb-12 text-left">
                  {{ plan.description }}
                </p>
                <div class="flex items-center justify-between min-h-[3rem]">
                  <div class="flex items-baseline gap-1.5">
                    <span class="text-3xl font-semibold text-black/90">
                      {{ formatPrice(currentPrice(plan)) }}
                    </span>
                    <span v-if="currentPrice(plan) > 0" class="text-sm text-black/40">
                      <span class="text-[10px] leading-none font-bold px-0.5 align-baseline">/</span
                      >{{ t('pricing.page.price.perMonth') }}
                    </span>
                  </div>
                  <span
                    v-if="
                      currentPrice(plan) > 0 &&
                      billingPeriod === 'yearly' &&
                      yearlySavings(plan) > 0
                    "
                    class="text-base font-semibold bg-[#3589ff]/10 text-[#3589ff] px-2.5 py-1 rounded-xl whitespace-nowrap"
                  >
                    {{ t('pricing.page.price.savePerYear', { amount: yearlySavings(plan) }) }}
                  </span>
                </div>
              </div>

              <button
                v-if="(plan.id === 'pro' || plan.id === 'ultra') && isAuthenticated"
                type="button"
                :disabled="loadingPlanId === plan.id"
                @click="handleSubscribe(plan)"
                :class="[
                  'w-full py-2.5 rounded-xl font-normal transition-all duration-200 mb-2 text-center relative flex items-center justify-center',
                  plan.popular
                    ? 'text-white/90 bg-black/85 hover:bg-black'
                    : 'text-black/90 bg-black/5 hover:bg-black/10',
                  loadingPlanId === plan.id ? 'opacity-70 cursor-not-allowed' : '',
                ]"
              >
                <span v-if="loadingPlanId === plan.id" class="absolute">
                  <Loader2 class="animate-spin h-5 w-5 text-current" />
                </span>
                <span :class="{ 'opacity-0': loadingPlanId === plan.id }">{{ plan.cta }}</span>
              </button>

              <NuxtLink
                v-else
                :to="plan.ctaLink"
                :class="[
                  'w-full py-2.5 rounded-xl font-normal transition-all duration-200 mb-2 text-center block',
                  plan.popular
                    ? 'text-white/90 bg-black/85 hover:bg-black'
                    : 'text-black/90 bg-black/5 hover:bg-black/10',
                ]"
              >
                {{ plan.cta }}
              </NuxtLink>

              <!-- Credit Tier Selector for Pro Plan -->
              <div v-if="plan.id === 'pro'">
                <ClientOnly>
                  <Select
                    :model-value="selectedCreditTier.toString()"
                    @update:model-value="selectedCreditTier = Number($event)"
                  >
                    <template #fallback>
                      <div
                        class="w-full py-3 px-4 border-0 bg-black/5 rounded-xl text-sm font-normal text-black/90 h-auto"
                      >
                        {{ selectedCreditTier.toLocaleString() }}
                        {{ t('pricing.page.plans.pro.monthlyCreditsSuffix') }}
                      </div>
                    </template>
                    <SelectTrigger
                      class="w-full py-3 px-4 border-0 bg-black/5 hover:bg-black/10 rounded-xl text-sm font-normal text-black/90 focus:outline-none transition-all duration-200 h-auto"
                    >
                      <SelectValue>
                        {{ selectedCreditTier.toLocaleString() }}
                        {{ t('pricing.page.plans.pro.monthlyCreditsSuffix') }}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem
                          v-for="tier in proCreditTiers"
                          :key="tier.credits"
                          :value="tier.credits.toString()"
                        >
                          {{ tier.credits.toLocaleString() }}
                          {{ t('pricing.page.plans.pro.monthlyCreditsSuffix') }}
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <template #fallback>
                    <div
                      class="w-full py-3 px-4 border-0 bg-black/5 rounded-xl text-sm font-normal text-black/90 h-auto"
                    >
                      {{ selectedCreditTier.toLocaleString() }}
                      {{ t('pricing.page.plans.pro.monthlyCreditsSuffix') }}
                    </div>
                  </template>
                </ClientOnly>
              </div>

              <p class="text-sm font-medium text-black/70 mt-4 mb-3">
                <template v-if="plan.id === 'pro'">{{ t('pricing.page.includesPro') }}</template>
                <template v-else-if="plan.id === 'ultra'">{{
                  t('pricing.page.includesUltra')
                }}</template>
                <template v-else>{{ t('pricing.page.includes') }}</template>
              </p>
              <ul class="space-y-2 flex-1">
                <!-- Credits Info -->
                <template v-if="plan.id === 'free'">
                  <li
                    v-if="currentCredits(plan).daily > 0"
                    class="text-sm font-normal text-black/90 flex items-center"
                  >
                    <svg
                      class="w-4 h-4 text-black/60 mr-3 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span class="text-sm">{{
                      t('pricing.page.plans.free.features.dailyCredits', {
                        count: currentCredits(plan).daily,
                      })
                    }}</span>
                  </li>
                  <li
                    v-if="currentCredits(plan).bonus > 0"
                    class="text-sm font-normal text-black/90 flex items-center"
                  >
                    <svg
                      class="w-4 h-4 text-black/60 mr-3 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span class="text-sm">{{
                      t('pricing.page.plans.free.features.bonusCredits', {
                        count: currentCredits(plan).bonus,
                      })
                    }}</span>
                  </li>
                </template>
                <template v-else-if="plan.id === 'pro'">
                  <li
                    v-if="currentCredits(plan).daily > 0"
                    class="text-sm font-normal text-black/90 flex items-center"
                  >
                    <svg
                      class="w-4 h-4 text-black/60 mr-3 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span class="text-sm">{{
                      t('pricing.page.plans.free.features.dailyCredits', {
                        count: currentCredits(plan).daily,
                      })
                    }}</span>
                  </li>
                  <li class="text-sm font-normal text-black/90 flex items-center">
                    <svg
                      class="w-4 h-4 text-black/60 mr-3 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span class="text-sm">{{
                      t('pricing.page.plans.pro.dynamicMonthlyCredits', {
                        count: currentMonthlyCredits.toLocaleString(),
                      })
                    }}</span>
                  </li>
                </template>
                <template v-else-if="plan.id === 'ultra'">
                  <li
                    v-if="currentCredits(plan).daily > 0"
                    class="text-sm font-normal text-black/90 flex items-center"
                  >
                    <svg
                      class="w-4 h-4 text-black/60 mr-3 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span class="text-sm">{{
                      t('pricing.page.plans.free.features.dailyCredits', {
                        count: currentCredits(plan).daily,
                      })
                    }}</span>
                  </li>
                  <li
                    v-if="currentCredits(plan).monthly > 0"
                    class="text-sm font-normal text-black/90 flex items-center"
                  >
                    <svg
                      class="w-4 h-4 text-black/60 mr-3 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span class="text-sm">{{
                      t('pricing.page.plans.free.features.monthlyCredits', {
                        count: (currentCredits(plan).monthly || 0).toLocaleString(),
                      })
                    }}</span>
                  </li>
                </template>
                <li
                  v-for="(feature, index) in plan.features.filter(
                    (f) => f.text && f.text !== 'undefined' && !f.text.includes('undefined')
                  )"
                  :key="index"
                  :class="[
                    feature.sub || isSubFeature(feature.text)
                      ? 'text-sm text-black/50 pl-5 flex items-start'
                      : 'text-sm font-normal text-black/90 flex items-center',
                  ]"
                >
                  <template v-if="!feature.sub && !isSubFeature(feature.text)">
                    <svg
                      class="w-4 h-4 text-black/60 mr-3 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </template>
                  <template v-else>
                    <span class="text-black/30 mr-2 ml-2">•</span>
                  </template>
                  <span class="text-sm">
                    {{ feature.text }}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- FAQ Section -->
        <!-- eslint-disable-next-line vue/no-undef-components -->
        <PricingFaq />
      </div>
    </div>
    <AppFooter />
  </div>
</template>

<style scoped>
@keyframes gradientShift {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.popular-badge {
  background: linear-gradient(90deg, #11a0ff, #3589ff);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gradientShift 3s ease infinite;
}
</style>
